import { useCallback } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  MiniMap,
  useReactFlow,
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import WorkflowNodeCard from "./WorkflowNodeCard";
import AutomationEdge from "./AutomationEdge";
import type NodeDescriptor from "../../models/NodeDescriptor";
import type { WorkflowNodeData, WorkflowEdgeData } from "./types";

const nodeTypes = { automationNode: WorkflowNodeCard };
const edgeTypes = { automationEdge: AutomationEdge };

export const AUTOMATION_NODE_DRAG_TYPE = "application/automation-node";

interface AutomationCanvasProps {
  nodes: Node<WorkflowNodeData>[];
  edges: Edge<WorkflowEdgeData>[];
  onNodesChange: OnNodesChange<Node<WorkflowNodeData>>;
  onEdgesChange: OnEdgesChange<Edge<WorkflowEdgeData>>;
  onConnect: OnConnect;
  onNodeClick: (nodeId: string) => void;
  onEdgeClick: (edgeId: string) => void;
  onPaneClick: () => void;
  onDropNode: (descriptor: NodeDescriptor, position: { x: number; y: number }) => void;
  /** Fired after Delete/Backspace removes the selected nodes/edges, so panels can close. */
  onDelete: () => void;
}

function CanvasBackground() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(circle, #94a3b8 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          opacity: 0.4,
        }}
      />
    </div>
  );
}


function CanvasInner({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onEdgeClick,
  onPaneClick,
  onDropNode,
  onDelete,
}: AutomationCanvasProps) {
  const { screenToFlowPosition } = useReactFlow();

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const raw = event.dataTransfer.getData(AUTOMATION_NODE_DRAG_TYPE);
      if (!raw) return;

      const descriptor: NodeDescriptor = JSON.parse(raw);
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      onDropNode(descriptor, position);
    },
    [screenToFlowPosition, onDropNode],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  return (
    <div className="flex-1 h-full relative bg-[#f0f0f3]" onDrop={handleDrop} onDragOver={handleDragOver}>
      <CanvasBackground />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{ type: "automationEdge", data: { condition: "", kind: "Normal" } }}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={(_, node) => onNodeClick(node.id)}
        onEdgeClick={(_, edge) => onEdgeClick(edge.id)}
        onPaneClick={onPaneClick}
        deleteKeyCode={["Delete", "Backspace"]}
        onDelete={onDelete}
        proOptions={{ hideAttribution: true }}
        fitView
        style={{ background: "transparent" }}
      >
        <Controls
          position="bottom-left"
          className="!bg-white/70 !backdrop-blur-md !border !border-black/5 !rounded-2xl !shadow-lg [&>button]:!border-black/5 [&>button]:!bg-transparent [&>button]:!rounded-xl [&>button]:hover:!bg-accent/5 [&>button]:!text-foreground/50 [&>button]:hover:!text-accent [&>button]:!transition-colors"
        />
        <MiniMap
          pannable
          zoomable
          position="bottom-right"
          className="!bg-white/60 !backdrop-blur-md !border !border-black/5 !rounded-2xl !shadow-lg"
          maskColor="rgba(240, 240, 243, 0.7)"
          nodeColor={(n) => {
            const type = (n.data as WorkflowNodeData)?.workflowNode?.type;
            if (type === "Switch") return "#f59e0b";
            if (type === "HttpRequest") return "#3b82f6";
            if (type === "InvokeWorkflow") return "#8b5cf6";
            if (type === "Delay") return "#14b8a6";
            return "#0071e3";
          }}
        />
      </ReactFlow>
    </div>
  );
}

export default function AutomationCanvas(props: AutomationCanvasProps) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}
